/**
 * Histórico do processo da OS nativa por ciclo: "Atendimento original" e "Retorno N".
 * Ciclo atual no topo (aberto); anteriores recolhidos e somente leitura.
 */

import React from 'react';
import { Play } from 'lucide-react';
import { OrderDetailsHistoricoProcessoV2 } from './OrderDetailsHistoricoProcessoV2';
import { formatReincidenciaDataAbertura } from '../utils/orderDetailsReincidenciaUtils';

const fmt = (v) => (v ? formatReincidenciaDataAbertura(v, true) : '—');

function signatureSrc(svg) {
  const s = String(svg || '').trim();
  return s.startsWith('data:image/svg') ? s : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`;
}

function Field({ label, children }) {
  return (
    <div className="odv2-ciclo-field">
      <span className="odv2-ciclo-label">{label}</span>
      <span className="odv2-ciclo-value">{children || '—'}</span>
    </div>
  );
}

function CicloBody({ cycle, steps }) {
  return (
    <div className="odv2-ciclo-body">
      <OrderDetailsHistoricoProcessoV2 steps={steps} loading={false} error={null} />
      <div className="odv2-ciclo-grid">
        <Field label="Técnico">{cycle.technician}</Field>
        <Field label="Condição">{cycle.condition}</Field>
        <Field label="Início">{cycle.startedAt && fmt(cycle.startedAt)}</Field>
        <Field label="Fim">{cycle.finishedAt && fmt(cycle.finishedAt)}</Field>
        <Field label="Efetivação">{cycle.effectivatedAt && fmt(cycle.effectivatedAt)}</Field>
        <Field label="Pedido vinculado">{cycle.salesOrders?.join(', ')}</Field>
      </div>
      {cycle.technicalReport ? (
        <Field label="Laudo técnico">
          <span className="odv2-ciclo-text">{cycle.technicalReport}</span>
        </Field>
      ) : null}
      {cycle.parts?.length ? (
        <Field label="Peças">
          <ul className="odv2-ciclo-list">
            {cycle.parts.map((p, i) => (
              <li key={`${p.code}-${i}`}>
                {p.code}
                {p.name ? ` - ${p.name}` : ''} · {p.quantity}
                {p.salesOrder ? ` · Pedido ${p.salesOrder}` : ''}
              </li>
            ))}
          </ul>
        </Field>
      ) : null}
      {cycle.meters?.length ? (
        <Field label="Leituras">
          <ul className="odv2-ciclo-list">
            {cycle.meters.map((m) => (
              <li key={m.name}>
                {m.name}: {m.value}
              </li>
            ))}
          </ul>
        </Field>
      ) : null}
      {cycle.media?.length || cycle.signature ? (
        <div className="odv2-ciclo-media">
          {(cycle.media || []).map((m, i) => (
            <a key={m.url} href={m.url} target="_blank" rel="noopener noreferrer" className="odv2-ciclo-thumb">
              {m.isVideo ? (
                <Play size={18} strokeWidth={2} aria-label={`Vídeo ${i + 1}`} />
              ) : (
                <img src={m.url} alt={`Foto ${i + 1}`} loading="lazy" />
              )}
            </a>
          ))}
          {cycle.signature ? (
            <span className="odv2-ciclo-thumb odv2-ciclo-thumb--signature" title={cycle.signature.signerName || 'Assinatura'}>
              <img src={signatureSrc(cycle.signature.svg)} alt="Assinatura" />
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function OrderDetailsHistoricoCiclos({ cycles, steps }) {
  return (
    <div className="odv2-ciclos">
      {cycles.map((cycle) => {
        const cycleSteps = (steps || []).filter((s) => Number(s.cycle) === cycle.cycle);
        const title = (
          <span className="odv2-ciclo-title">
            {cycle.label}
            {cycle.isCurrent ? <span className="odv2-ciclo-badge">Atual</span> : null}
            {cycle.technician ? <span className="odv2-ciclo-sub">{cycle.technician}</span> : null}
          </span>
        );
        return cycle.isCurrent ? (
          <section key={cycle.cycle} className="odv2-ciclo odv2-ciclo--current">
            <header className="odv2-ciclo-head">{title}</header>
            <CicloBody cycle={cycle} steps={cycleSteps} />
          </section>
        ) : (
          <details key={cycle.cycle} className="odv2-ciclo">
            <summary className="odv2-ciclo-head">{title}</summary>
            <CicloBody cycle={cycle} steps={cycleSteps} />
          </details>
        );
      })}
    </div>
  );
}
